export type PresentationDeckPreview = {
  filename: string
  pdfFilename?: string
  previewImages: string[]
  slideCount: number
}

function basename(value: string): string {
  const clean = value.split(/[?#]/)[0]
  const parts = clean.split("/")
  return parts[parts.length - 1] || clean
}

function isPresentationFilename(value: string): boolean {
  return value.toLowerCase().endsWith(".pptx")
}

function isPresentationPreviewFilename(value: string): boolean {
  return /^preview[_-].*\.(?:png|jpe?g)$/i.test(basename(value))
}

function isPreviewImageLine(line: string, previewNames: Set<string>): boolean {
  const imageMatches = Array.from(line.matchAll(/!\[[^\]]*]\(([^)]+)\)/g))
  if (imageMatches.length === 0) return false
  const withoutImages = line.replace(/!\[[^\]]*]\([^)]+\)/g, "").trim()
  if (withoutImages.length > 0) return false
  return imageMatches.every((match) => previewNames.has(basename(match[1])))
}

export function normalizePresentationDecks(value: unknown): PresentationDeckPreview[] {
  if (!Array.isArray(value)) return []

  return value.flatMap((item) => {
    if (!item || typeof item !== "object") return []
    const record = item as Record<string, unknown>
    const filename = typeof record.filename === "string" ? record.filename : ""
    const previewImages = Array.isArray(record.preview_images)
      ? record.preview_images.filter((image): image is string => typeof image === "string")
      : []
    if (!filename || !isPresentationFilename(filename) || previewImages.length === 0) return []

    const pdfFilename = typeof record.pdf_filename === "string" ? record.pdf_filename : undefined
    const slideCount =
      typeof record.slide_count === "number" && Number.isFinite(record.slide_count)
        ? record.slide_count
        : previewImages.length

    return [
      {
        filename,
        pdfFilename,
        previewImages,
        slideCount,
      },
    ]
  })
}

export function inferPresentationDecksFromFiles(filenames: string[]): PresentationDeckPreview[] {
  const filename = filenames.find(isPresentationFilename)
  if (!filename) return []

  const previewImages = filenames.filter(isPresentationPreviewFilename)
  if (previewImages.length === 0) return []

  return [
    {
      filename,
      previewImages,
      slideCount: previewImages.length,
    },
  ]
}

export function stripPresentationPreviewMarkdown(
  content: string,
  decks: PresentationDeckPreview[]
): string {
  if (decks.length === 0) return content

  const previewNames = new Set(
    decks.flatMap((deck) => deck.previewImages.map((image) => basename(image)))
  )
  if (previewNames.size === 0) return content

  const lines = content.split("\n")
  const keptLines = lines.filter((line) => !isPreviewImageLine(line, previewNames))

  if (keptLines.length === lines.length) return content

  return keptLines
    .filter((line) => !/^\s*Here are (?:the )?slide previews:\s*$/i.test(line))
    .join("\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim()
}
